import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { Product, ProductVariant } from '../../types/product-variant';
import CartActions from './CartActions';
import { motion } from 'framer-motion';
import ShippingInfo from './ShippingInfo'
import ShareButton from './ShareButton';
import ProductTrustSignals from './ProductTrustSignals';
import { useStock } from '../../context/StockContext';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import RingSizeSelector, { SizeInventory } from './RingSizeSelector';
import { RING_SIZES } from './RingSizeGuide';

// Adjustable rings — open-ended design, no size selection needed
const ADJUSTABLE_RING_SLUGS = ['pez-fontana-r', 'lapislazuli-pez'];

// Per-size stock status for rings
// Lava Ring: US 5.5→EU 51 (×1), US 6→EU 52 (×3), US 6.5→EU 53 (×3), rest made-to-order
// Dos Caras: 1 in stock, resizable to any EU size
const RING_SIZE_INVENTORY: Record<string, SizeInventory> = {
    'lava-ring': Object.fromEntries(
        RING_SIZES.map(({ eu }) => [eu, [51, 52, 53].includes(eu) ? 'in_stock' : 'made_to_order'])
    ) as SizeInventory,
    'dos-caras': Object.fromEntries(
        RING_SIZES.map(({ eu }) => [eu, eu === 58 ? 'in_stock' : 'made_to_order'])
    ) as SizeInventory,
};

interface ProductInfoProps {
    product: Product;
    selectedVariant: ProductVariant | null;
    isAdded: boolean;
    localQuantity: number;
    onAddToCart: () => void;
    onQuantityChange: (quantity: number) => void;
    showVariantError: boolean;
    onVariantChange: (variantId: string) => void;
    inStock?: boolean;
    stockError: string | null;
    imageUrl?: string;
    ringSize?: string;
    onRingSizeChange?: (size: string) => void;
    showRingSizeError?: boolean;
}

const ProductInfo = ({
    product,
    selectedVariant,
    isAdded,
    localQuantity,
    onAddToCart,
    onQuantityChange,
    showVariantError,
    onVariantChange,
    stockError,
    imageUrl,
    ringSize = '',
    onRingSizeChange,
    showRingSizeError = false,
}: ProductInfoProps) => {
    const hasVariants = product?.variants && product.variants.length > 0;
    const minVariantPrice = hasVariants ? Math.min(...product.variants!.map(v => v.price)) : 0;
    const activePrice = selectedVariant?.price ?? (hasVariants ? minVariantPrice : (product?.price ?? 0));
    const { getStock, isMadeToOrder } = useStock();
    const { t } = useTranslation();
    const actualStock = getStock(product.id, selectedVariant?.id);
    const madeToOrder = isMadeToOrder(product.id, selectedVariant?.id);

    // Per-size stock override for rings with sizeInventory
    const sizeInventory = RING_SIZE_INVENTORY[product.slug];
    const selectedEuSize = ringSize ? parseInt(ringSize.replace('EU ', '')) : null;
    const sizeStatus = (selectedEuSize && sizeInventory) ? sizeInventory[selectedEuSize] : null;

    // When a ring size is selected, override stock display based on per-size inventory
    const isProductInStock = sizeStatus ? sizeStatus === 'in_stock' : actualStock > 0;
    const isMadeToOrderAvailable = sizeStatus ? sizeStatus === 'made_to_order' : actualStock === 0 && madeToOrder;
    const isOutOfStock = sizeStatus ? sizeStatus === 'out_of_stock' : actualStock === 0 && !madeToOrder;

    // CartActions overrides — only when a ring size with sizeInventory is selected
    const stockOverride = sizeStatus === 'in_stock' ? 1 : sizeStatus === 'made_to_order' ? 0 : sizeStatus === 'out_of_stock' ? 0 : undefined;
    const madeToOrderOverride = sizeStatus === 'made_to_order' ? true : sizeStatus ? false : undefined;

    // For products with variants, only check stock after variant is selected
    const shouldShowStockStatus = product.variants && product.variants.length > 0
        ? selectedVariant !== null
        : true;
    
    const MaterialTag = () => {
        if (product.variants && product.variants.length > 0) {
            return selectedVariant ? (
                <span className="px-3 py-1 text-sm bg-gray-200 dark:bg-brand-charcoal text-black dark:text-white rounded-full">
                    {t(`materials.${selectedVariant.material}`, selectedVariant.material)}
                </span>
            ) : (
                <span className="px-3 py-1 text-sm bg-gray-200 dark:bg-brand-charcoal text-black dark:text-white rounded-full">
                    {t('product.multipleMaterials')}
                </span>
            );
        }
        
        return product.material ? (
            <span className="px-3 py-1 text-sm bg-gray-200 dark:bg-brand-charcoal text-black dark:text-white rounded-full">
                {t(`materials.${product.material}`, product.material)}
            </span>
        ) : null;
    };

    const VariantSelector = () => (
        product?.variants && product.variants.length > 0 && (
            <div className="mb-6">
                <label className="block text-black dark:text-white mb-2">
                    {t('product.selectMaterial')}
                </label>
                <select 
                    className="w-full p-2 border rounded-lg bg-white dark:bg-brand-charcoal text-black dark:text-white"
                    onChange={(e) => onVariantChange(e.target.value)}
                    value={selectedVariant?.id || ''}
                >
                    <option value="">{t('product.selectMaterialPlaceholder')}</option>
                    {product.variants.map(variant => (
                        <option key={variant.id} value={variant.id}>
                            {t(`materials.${variant.material}`, variant.material)} - €{variant.price.toFixed(2)}
                        </option>
                    ))}
                </select>
            </div>
        )
    );

    return (
        <div className="space-y-4 md:space-y-6 pb-0 md:pb-0">
            <h1 className="text-3xl md:text-4xl font-bold text-black dark:text-white">
                {product.name}
            </h1>
            <div className="flex items-center justify-between">
                <p className="text-xl text-black dark:text-white">
                    {hasVariants && !selectedVariant ? `${t('product.from')} ` : ''}€{activePrice.toFixed(2)}
                </p>
                <ShareButton 
                    url={window.location.href}
                    title={product.name}
                    description={product.description}
                    image={imageUrl || ''}
                />
            </div>
            {product.oneOfKind && (
                <div className="flex items-center gap-2 text-brand-blue">
                    <FontAwesomeIcon icon={faStar} className="h-4 w-4" />
                    <p className="font-semibold text-lg">
                        {t('product.oneOfKind')}
                    </p>
                </div>
            )}
            <VariantSelector />

            {/* Ring Size Selector — only for rings category, hidden when out of stock */}
            {product.category === 'Rings' && onRingSizeChange && !isOutOfStock && (
                <RingSizeSelector
                    selectedSize={ringSize}
                    onSizeChange={onRingSizeChange}
                    isAdjustable={ADJUSTABLE_RING_SLUGS.includes(product.slug)}
                    sizeInventory={RING_SIZE_INVENTORY[product.slug]}
                />
            )}

            {/* Stock Status Badges */}
            {shouldShowStockStatus && isProductInStock && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-500 font-semibold mb-4">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    {t('product.inStock')}
                </div>
            )}

            {shouldShowStockStatus && isMadeToOrderAvailable && (
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500 font-semibold mb-4">
                    <FontAwesomeIcon icon={faClock} className="h-4 w-4" />
                    {t('product.madeToOrder')}
                    <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                        {t('product.madeToOrderTime')}
                    </span>
                </div>
            )}

            {shouldShowStockStatus && isOutOfStock && (
                <div className="text-red-500 font-semibold mb-4">
                    {t('product.outOfStock')}
                </div>
            )}

            <CartActions
                product={product}
                isAdded={isAdded}
                localQuantity={localQuantity}
                onAddToCart={onAddToCart}
                onQuantityChange={onQuantityChange}
                showVariantError={showVariantError}
                inStock={isProductInStock}
                selectedVariant={selectedVariant}
                stockOverride={stockOverride}
                madeToOrderOverride={madeToOrderOverride}
                needsRingSize={product.category === 'Rings' && !ADJUSTABLE_RING_SLUGS.includes(product.slug) && !isOutOfStock && !ringSize}
            />

            {stockError && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-red-500 text-sm mt-2"
                >
                    {stockError}
                </motion.div>
            )}

            {showRingSizeError && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-red-500 text-sm"
                >
                    {t('ringSize.pleaseSelectSize')}
                </motion.div>
            )}

            {/* Trust Signals */}
            <ProductTrustSignals />

            {/* Tags Section */}
            <div className="flex flex-wrap gap-2">
                <MaterialTag />
                <span className="px-3 py-1 text-sm bg-gray-200 dark:bg-brand-charcoal text-black dark:text-white rounded-full">
                    {t(`categories.${product.category}`, product.category)}
                </span>
            </div>

            {/* Description Label */}
            <h2 className="text-2xl font-semibold text-black dark:text-white">
                {t('product.description')}
            </h2>
            {/* Description */}
            <div className="space-y-2">
                <p className="text-black dark:text-white">
                    {product.description}
                </p>
            </div>
            
            {/* Shipping Information */}
            <ShippingInfo />
        </div>
    );
};

export default ProductInfo; 